import React, { useState, useEffect, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import _get from "lodash/get";
import { Formik } from "formik";

import Button from "components/lib/Shared/Button";
import Tag from "components/lib/Shared/Tag";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import ReactTable from "components/lib/Shared/ReactTable";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { CheckIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import { INDIVIDUALS } from "components/PLCTABS/SFLdata/config/constants";
import { addComment, editComment, getComments } from "state/slices/comment";
import { resolveFields, invalidateFields } from "state/slices/resolveField";
import { Constants } from "config/constants";
import CommentsList from "components/lib/Global/CommentsList";
import ShareAllocationSelection from "./ShareAllocationSelection";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import DeleteButtonWithConfirmation from "components/lib/Shared/DeleteButtonWithConfirmation";
import ConfirmDialog from "components/lib/Shared/ConfirmDialog";
import OfficialsBeneficialOwnershipComponent from "components/PLCTABS/CompanyOfficialsTab/BeneficialOwnershipComponents/OfficialsBeneficialOwnershipComponent";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import MainShareAllocationComponent from "components/PLCTABS/CompanyOfficialsTab/ShareAllocationComponents";

function OfficialShareAllocation({
  row,
  isViewMode,
  officialSliceName,
  companyOfficialEffectKey,
  officialShareAllocationComments,
  currentTab = "stakeholderTab",
  isChangesList,
  remainderAmounts,
}) {
  const [showAllocateSharesBtn, setAllocateSharesBtn] = useState(true);
  const [editableShareAlloc, setEditableShareAlloc] = useState(null);

  const { transactions } = useContext(UpdateCompanyDetailsContext);

  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.userSlice);
  const { company } = useSelector((state) => state.companySlice);
  const { modalRow } = useSelector((state) => state.modalsSlice);

  // approve/disapprove allotted shares to official
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRow] = useState([]);
  const [allFieldsResolved, setAllFieldsResolved] = useState(false);

  const isReviewerMode =
    history.location.pathname.split("/").pop() === "review";
  const isReviewer = Cookies.get("role") === Constants.COOKIE_ROLES.REVIEWER;

  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );
  const { shares } = useSelector((state) => state.sharesAllocationTableSlice);

  const alreadyTakenClassOfShares = shares.map((share) => share.classOfShares);

  let classOfSharesOptions = useMemo(() => {
    return nominalShareCapital.map(({ sharesCategory }) => ({
      label: sharesCategory,
      value: sharesCategory,
    }));
  }, [nominalShareCapital]);

  const [selectedClassOfShare, setSelectedClassOfShare] = useState("");

  useEffect(() => {
    if (editableShareAlloc?.classOfShares) {
      setSelectedClassOfShare(
        classOfSharesOptions.find(
          (classOfShares) =>
            classOfShares.value === editableShareAlloc?.classOfShares
        )
      );
    }
  }, [classOfSharesOptions, editableShareAlloc?.classOfShares]);

  const formId = _get(row.original, "_id");

  const fieldsName = row.original?.sharesAllotted
    ?.map((share) => share.classOfShares)
    .filter(Boolean); // all the shares category names

  const resolvedFields = _get(row.original, "resolvedFields", []);

  useEffect(() => {
    setAllFieldsResolved(
      fieldsName?.every((element) => resolvedFields.includes(element))
    );
  }, [fieldsName, resolvedFields]);

  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            officialId: row.original._id,
            tabName: currentTab,
          },
        })
      );
    }
  };

  const officialShareAllocationColumns = [
    {
      Header: "Class of shares",
      Cell: ({ row }) => row.original.classOfShares,
    },
    { Header: "Number of Shares", accessor: "allottedShares" },
    {
      Header: "Value Of Shares",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.sharesValue}</Tag>
        </div>
      ),
    },
    { Header: "Paid Up Shares", accessor: "paidUpShares" },
    {
      Header: "Premium Paid",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.sharePremium}</Tag>
        </div>
      ),
    },
    {
      Header: "Nominal Value",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.nominalValue}</Tag>
        </div>
      ),
    },
    {
      Header: "Voting Rights",
      Cell: ({ row }) => row.original.votingRights,
    },
  ];

  const handleShareEdit = (row) => {
    setEditableShareAlloc(row);
  };

  const handleShareDelete = async (shareRow) => {
    setEditableShareAlloc(null);
    await dispatch(
      updateCompanyOfficialAsync({
        officialId: row.original._id,
        data: {
          companyId: company._id,
          idType: row.original.idType,
          sharesAllotted: shares.filter((share) => share._id !== shareRow._id),
          beneficialOwnershipForm: {
            ...row.original.beneficialOwnershipForm,
            directPercentShareholding: "",
            indirectPercentShareholding: "",
            directPercentVotingRights: "",
            indirectPercentVotingRights: "",
            dateOfBecomingBO: "",
          },
        },
      })
    );
  };

  if (!isViewMode) {
    officialShareAllocationColumns.push({
      Header: "Action",
      Cell: ({ row }) => {
        const isRowLoading =
          isLoading && selectedRows.includes(row.original.classOfShares);
        return (
          <div className="flex items-center space-x-2">
            <Button
              className="items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
              onClick={() => handleShareEdit(row.original)}
              disabled={resolvedFields.includes(row.original.classOfShares)}
            >
              Edit
            </Button>
            <DeleteButtonWithConfirmation
              confirmationModalName="delete_share_allocation_confirmation_modal"
              row={row.original}
              disabled={resolvedFields.includes(row.original.classOfShares)}
              btnClasses="items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:cursor-not-allowed"
            />
            {!resolvedFields.includes(row.original.classOfShares) && (
              <CommentsPopup
                handleSaveComment={handleSaveComment}
                formName="officialShareAllocation"
                fieldName={row.original.classOfShares}
                fieldLabel={row.original.classOfShares}
                comment={officialShareAllocationComments.find(
                  (comment) =>
                    comment.fieldName === row.original.classOfShares &&
                    currentUser?._id === comment.createdBy?._id
                )}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className={`flex items-center justify-center pr-4`}>
              {resolvedFields.includes(row.original.classOfShares) ? (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleInvalidateBtnClicked(row.original.classOfShares)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted </span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleResolveBtnClicked(row.original.classOfShares)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  const handleResolveBtnClicked = async (fieldName) => {
    // approve field
    const commentsToBeResolved = officialShareAllocationComments?.filter(
      (comment) => comment.fieldName === fieldName && !comment.isResolved
    );
    const commentsToBeResolvedIds = commentsToBeResolved?.map(({ _id }) => _id);
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      resolveFields({
        data: {
          formId,
          formName:
            currentTab === "stakeholderTab"
              ? INDIVIDUALS.includes(row?.original?.idType)
                ? "individualOfficial"
                : "corporateOfficial"
              : "beneficialOwner",
          fieldName,
          resolvedFields: [fieldName],
          commentsToBeResolvedIds,
        },
        updateEffectKey: companyOfficialEffectKey,
        updateEffectPath: officialSliceName,
      })
    );
    if (commentsToBeResolvedIds.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const handleInvalidateBtnClicked = async (fieldName) => {
    // disapprove field
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId,
          formName:
            currentTab === "stakeholderTab"
              ? INDIVIDUALS.includes(row?.original?.idType)
                ? "individualOfficial"
                : "corporateOfficial"
              : "beneficialOwner",
          fieldName,
          invalidFields: [fieldName],
        },
        updateEffectKey: companyOfficialEffectKey,
        updateEffectPath: officialSliceName,
      })
    );
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const handleAllFieldsStatus = async (e) => {
    const commentsToBeResolved = officialShareAllocationComments?.filter(
      (comment) => !comment.isResolved
    );
    const commentsToBeResolvedIds = commentsToBeResolved?.map(({ _id }) => _id);
    setIsLoading(true);
    if (e.target.checked) {
      // resolve/approve all the form fields
      const unresolvedFields = fieldsName.filter(
        (fieldName) => !resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [...selectedRows, ...unresolvedFields]);
      await dispatch(
        resolveFields({
          data: {
            formId,
            formName:
              currentTab === "stakeholderTab"
                ? INDIVIDUALS.includes(row?.original?.idType)
                  ? "individualOfficial"
                  : "corporateOfficial"
                : "beneficialOwner",
            resolvedFields: unresolvedFields,
            commentsToBeResolvedIds,
          },
          updateEffectKey: companyOfficialEffectKey,
          updateEffectPath: officialSliceName,
        })
      );
      if (commentsToBeResolvedIds.length > 0) {
        const query = { companyId: commentsToBeResolved[0]?.company };
        if (isReviewerMode) query.createdBy = currentUser?._id;
        await dispatch(getComments(query));
      }
      setIsLoading(false);
      setSelectedRow([]);
    } else {
      // invalidate/disapprove all the form fields
      const resolvedFieldsSlice = fieldsName.filter((fieldName) =>
        resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [
        ...selectedRows,
        ...resolvedFieldsSlice,
      ]);
      await dispatch(
        invalidateFields({
          data: {
            formId,
            formName:
              currentTab === "stakeholderTab"
                ? INDIVIDUALS.includes(row?.original?.idType)
                  ? "individualOfficial"
                  : "corporateOfficial"
                : "beneficialOwner",
            invalidFields: resolvedFieldsSlice,
          },
          updateEffectKey: companyOfficialEffectKey,
          updateEffectPath: officialSliceName,
        })
      );
      setSelectedRow([]);
    }
    setIsLoading(false);
  };

  const showShareAllocationSelection =
    !showAllocateSharesBtn || editableShareAlloc;

  const availableClassOfSharesOptions = classOfSharesOptions.filter(
    (option) => !alreadyTakenClassOfShares.includes(option.value)
  );

  if (showShareAllocationSelection) {
    return (
      <div className="px-8 pt-5 pb-6 my-6 bg-white">
        <ShareAllocationSelection
          companyId={company._id}
          setAllocateSharesBtn={setAllocateSharesBtn}
          editableShareAlloc={editableShareAlloc}
          setEditableShareAlloc={setEditableShareAlloc}
          currentOfficial={row.original}
          alreadyTakenClassOfShares={alreadyTakenClassOfShares}
          selectedClassOfShare={selectedClassOfShare}
          setSelectedClassOfShare={setSelectedClassOfShare}
        />
      </div>
    );
  }

  // Helper function to extract shares from transactions (same as main table)
  const getSharesFromTransactions = (
    officialId,
    shareCapitalChanges,
    nominalShareCapital = []
  ) => {
    if (!officialId || !Array.isArray(shareCapitalChanges)) {
      return [];
    }

    const sharesByClass = [];

    shareCapitalChanges.forEach((transaction) => {
      if (transaction.allocations && Array.isArray(transaction.allocations)) {
        const officialAllocation = transaction.allocations.find(
          (allocation) => String(allocation.officialId) === String(officialId)
        );

        if (officialAllocation && transaction.classOfShares) {
          const allocatedShares = Number(
            officialAllocation.allocatedShares || 0
          );
          const classOfShares = transaction.classOfShares;

          if (allocatedShares > 0) {
            // Lookup nominal share details for votesPerShare
            const nominalShare = nominalShareCapital.find(
              (ns) =>
                ns.sharesCategory === classOfShares ||
                ns._id === transaction.nominalShareId
            ) || { votesPerShare: 1 }; // Fallback to 1 vote per share

            // Per-share values
            const sharesValue =
              Number(transaction.offerPricePerShare || 0) -
              Number(transaction.premiumPerShare || 0);
            const sharePrice = Number(transaction.offerPricePerShare || 0);
            const sharePremium = Number(transaction.premiumPerShare || 0);
            const votingRights = allocatedShares * nominalShare.votesPerShare;

            // Create a share allocation object in the expected format
            sharesByClass.push({
              id: `${transaction._id}-${officialAllocation._id}`, // Unique ID for table row
              classOfShares: classOfShares,
              allottedShares: allocatedShares,
              sharesValue, // Per-share nominal value (e.g., 120 or 100)
              paidUpShares:
                Number(officialAllocation.paidUpShares || 0) || allocatedShares, // Override to allotted if 0
              sharePrice,
              sharePremium,
              nominalValue: allocatedShares * sharesValue, // Total nominal (e.g., 25 * 120 = 3000)
              votingRights,
              // Optional: Preserve additional fields
              effectiveDate: transaction.dateOfOffer,
              beneficialOwnershipDetails:
                officialAllocation.beneficialOwnershipDetails,
            });
          }
        }
      }
    });

    return sharesByClass;
  };

  // For changes list, show a simplified view with ownership structure
  if (isChangesList) {
    // Create data structure from transaction changes
    let changesListData = [];

    // First try to extract shareCapitalChanges from transactions context
    let shareCapitalChanges = [];
    if (transactions && Array.isArray(transactions)) {
      // Aggregate shareCapitalChanges from all transactions
      transactions.forEach((transaction) => {
        if (
          transaction.shareCapitalChanges &&
          Array.isArray(transaction.shareCapitalChanges)
        ) {
          shareCapitalChanges = shareCapitalChanges.concat(
            transaction.shareCapitalChanges
          );
        }
      });
    }

    // Use the new function to extract shares data
    if (shareCapitalChanges.length > 0 && row.original._id) {
      changesListData = getSharesFromTransactions(
        row.original._id,
        shareCapitalChanges
      );
    }

    // Extract beneficial ownership details from transaction
    const getTransactionBeneficialOwnershipDetails = () => {
      if (!transactions || !Array.isArray(transactions)) return null;

      // Find the transaction that matches this row's transaction ID
      const matchingTransaction = transactions.find(
        (tx) =>
          tx._id === row.original.transaction || tx._id === row.original._id
      );

      if (!matchingTransaction?.shareCapitalChanges) return null;

      for (const shareChange of matchingTransaction.shareCapitalChanges) {
        if (shareChange.allocations && Array.isArray(shareChange.allocations)) {
          const allocation = shareChange.allocations.find(
            (alloc) => String(alloc.officialId) === String(row.original._id)
          );

          if (allocation?.beneficialOwnershipDetails?.updatedDetails) {
            // Map the transaction data to the expected beneficialOwnershipForm structure
            const updatedDetails =
              allocation.beneficialOwnershipDetails.updatedDetails;
            const mappedBODetails = {
              // Map the updated details to the flat structure expected by OfficialsBeneficialOwnershipComponent
              directPercentShareholding:
                updatedDetails.directPercentShareholding || "",
              indirectPercentShareholding:
                updatedDetails.indirectPercentShareholding || "",
              directPercentVotingRights:
                updatedDetails.directPercentVotingRights || "",
              indirectPercentVotingRights:
                updatedDetails.indirectPercentVotingRights || "",
              directRightRemoveDirector:
                updatedDetails.directRightRemoveDirector || "no",
              indirectRightRemoveDirector:
                updatedDetails.indirectRightRemoveDirector || "no",
              directCompanyControlRight:
                updatedDetails.directCompanyControlRight || "no",
              indirectCompanyControlRight:
                updatedDetails.indirectCompanyControlRight || "no",
              dateOfBecomingBO: updatedDetails.dateOfBecomingBO || "",
              sourceOfBOInformation: updatedDetails.sourceOfBOInformation || "",
              maxPercentShareholding:
                updatedDetails.maxPercentShareholding || "",
              maxPercentVotingRights:
                updatedDetails.maxPercentVotingRights || "",
              // Preserve resolvedFields from original data if available
              resolvedFields:
                row.original.beneficialOwnershipForm?.resolvedFields || [],
            };

            return mappedBODetails;
          }
        }
      }

      return null;
    };

    const transactionBODetails = getTransactionBeneficialOwnershipDetails();

    // Fallback to old logic only if no data was extracted from transactions
    if (changesListData.length === 0) {
      if (
        row.original.allocatedShares !== undefined &&
        row.original.classOfShares
      ) {
        // For individual row changes (when row has transaction-specific data)
        const allocatedShares = Number(row.original.allocatedShares || 0);

        const changeItem = {
          classOfShares: row.original.classOfShares,
          allottedShares: row.original.allocatedShares,
          offerPricePerShare: row.original.offerPricePerShare || "0.00",
          sharePremium: row.original.premiumPerShare || "0.00", // Premium paid
          premiumPerShare: row.original.premiumPerShare || "0.00", // Fallback property
          paidUpShares: row.original.allocatedShares, // Assuming fully paid
          votingRights: allocatedShares, // Use number of shares for voting rights
          sharesValue:
            allocatedShares * (Number(row.original.offerPricePerShare) || 0),
          nominalValue:
            allocatedShares *
            (Number(row.original.AmountCreditedAsPaidUpCapitalPerShare) || 0),
        };
        changesListData.push(changeItem);
      } else if (row.original.sharesAllotted?.length > 0) {
        // Fallback to original shares allotted if transaction data not available
        changesListData.push(...row.original.sharesAllotted);
      }
    }

    return (
      <div className="mt-4">
        {officialShareAllocationComments &&
          officialShareAllocationComments.length > 0 && (
            <div className="px-8 pt-5 pb-6 my-6 bg-white">
              <CommentsList
                comments={officialShareAllocationComments}
                toggleCommentStatus
                applicantReviewer
              />
              <p className="mt-6 text-sm text-primary">
                Comments: {officialShareAllocationComments.length} of{" "}
                {officialShareAllocationComments.length}
              </p>
            </div>
          )}
        {changesListData.length > 0 ? (
          <div>
            <h3 className="mb-4 text-md font-semibold">New Allotted Shares</h3>

            {/* Share Allocation Component for Changes List with Edit Functionality */}
            <div className="mb-6">
              <Formik
                enableReinitialize
                initialValues={{
                  sharesAllotted: changesListData,
                  beneficialOwnershipForm:
                    transactionBODetails ||
                    row.original.beneficialOwnershipForm ||
                    {},
                }}
                onSubmit={() => {}}
              >
                {(formikProps) => {
                  // Update the form values with changesListData to ensure it's available
                  if (formikProps.values.sharesAllotted !== changesListData) {
                    formikProps.setFieldValue(
                      "sharesAllotted",
                      changesListData
                    );
                  }

                  return (
                    <MainShareAllocationComponent
                      currentOfficialId={row.original._id}
                      isCorporate={!INDIVIDUALS.includes(row?.original?.idType)}
                      resolvedFields={[]} // No resolved fields in changes list
                      disabled={false} // Allow editing
                      remainderAmounts={remainderAmounts}
                      transactions={transactions}
                      row={row}
                      editableShareAlloc={null}
                      autoOpenSelection={false}
                      onSubmissionComplete={() => {}}
                      isInChangesList={isChangesList}
                      onEditManualAllocation={() => {}}
                      onClearManualAllocation={() => {}} // Disable clear functionality
                      showEditOnly={true} // Custom prop to show only edit, no delete/clear
                      changesListData={changesListData} // Pass the changes list data
                    />
                  );
                }}
              </Formik>
            </div>

            {/* Beneficial Ownership Component below the table */}
            <div className="mt-6">
              <h3 className="mb-4 text-md font-semibold">
                Beneficial Ownership Details
              </h3>
              <Formik
                enableReinitialize
                initialValues={{
                  beneficialOwnershipForm:
                    transactionBODetails ||
                    row.original.beneficialOwnershipForm ||
                    {},
                }}
                onSubmit={() => {}}
              >
                {(formikProps) => {
                  return (
                    <OfficialsBeneficialOwnershipComponent
                      field={{
                        name: "beneficialOwnershipForm",
                        value:
                          transactionBODetails ||
                          row.original.beneficialOwnershipForm ||
                          {},
                      }}
                      form={formikProps}
                      corporateIdType={
                        !INDIVIDUALS.includes(row?.original?.idType)
                      }
                      isBOTab={false}
                      disableChangeEffect={true}
                      selectedShareholder={row.original}
                      disableDirectFields={false}
                      isReportable={false}
                      isInManualUpdate={false}
                      isinChangesList={true}
                      transactions={transactions}
                      row={row}
                      disabled={false}
                      resolvedFields={_get(
                        row.original.beneficialOwnershipForm,
                        "resolvedFields",
                        []
                      )}
                    />
                  );
                }}
              </Formik>
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-white shadow p-4">
              <p className="text-center text-gray-500">
                No share allocation data available for this change
              </p>
            </div>

            {/* Beneficial Ownership Component below the no-data message */}
            <div className="mt-6">
              <h3 className="mb-4 text-lg font-semibold">
                Beneficial Ownership Details
              </h3>
              <Formik
                enableReinitialize
                initialValues={{
                  beneficialOwnershipForm:
                    transactionBODetails ||
                    row.original.beneficialOwnershipForm ||
                    {},
                }}
                onSubmit={() => {}}
              >
                {(formikProps) => {
                  return (
                    <OfficialsBeneficialOwnershipComponent
                      field={{
                        name: "beneficialOwnershipForm",
                        value:
                          transactionBODetails ||
                          row.original.beneficialOwnershipForm ||
                          {},
                      }}
                      form={formikProps}
                      corporateIdType={
                        !INDIVIDUALS.includes(row?.original?.idType)
                      }
                      isBOTab={false}
                      disableChangeEffect={true}
                      selectedShareholder={row.original}
                      disableDirectFields={false}
                      isReportable={false}
                      isInManualUpdate={false}
                      transactions={transactions}
                      row={row}
                      disabled={false}
                      resolvedFields={_get(
                        row.original.beneficialOwnershipForm,
                        "resolvedFields",
                        []
                      )}
                    />
                  );
                }}
              </Formik>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <ConfirmDialog
        name="delete_share_allocation_confirmation_modal"
        onConfirm={() => handleShareDelete(modalRow)}
      >
        Are you sure you want to permanently delete this entry?
      </ConfirmDialog>
      {availableClassOfSharesOptions.length > 0 && (
        <Button
          className="mb-2"
          disabled={availableClassOfSharesOptions.length === 0}
          onClick={() => {
            setAllocateSharesBtn(false);
          }}
        >
          Allot Shares
        </Button>
      )}
      {officialShareAllocationComments &&
        officialShareAllocationComments.length > 0 && (
          <div className="px-8 pt-5 pb-6 my-6 bg-white">
            <CommentsList
              comments={officialShareAllocationComments}
              toggleCommentStatus
              applicantReviewer
            />
            <p className="mt-6 text-sm text-primary">
              Comments: {officialShareAllocationComments.length} of{" "}
              {officialShareAllocationComments.length}
            </p>
          </div>
        )}
      {row.original.sharesAllotted?.length > 0 ? (
        <>
          <div className="bg-white shadow overflow-auto">
            <ReactTable
              columns={officialShareAllocationColumns}
              data={row.original.sharesAllotted}
            />
          </div>
          {!isReviewer && (
            <div className="flex items-center mt-2">
              <input
                aria-describedby="comments-description"
                type="checkbox"
                className="mr-2 text-indigo-600 border-grayw-300 rounded focus:ring-indigo-500"
                onChange={handleAllFieldsStatus}
                checked={allFieldsResolved}
              />
              <p className="flex items-center text-sm font-semibold">
                Click this checkbox to{" "}
                {allFieldsResolved ? "disapprove" : "approve"} all entries if
                they are {allFieldsResolved ? "incorrect" : "correct"} then
                proceed to next step
              </p>
            </div>
          )}
        </>
      ) : (
        <p className="mt-4 text-center">No data available</p>
      )}
    </div>
  );
}

export default OfficialShareAllocation;
